// Recipe diagrams in the style of Cooking for Engineers: a fenced ```recipe
// block holding an outline, the finished dish first and what goes into each
// step indented under it:
//
//   ```recipe
//   bake 180°C 30 min
//     fold in
//       sift
//         2 cups flour
//         1 tsp salt
//       whisk
//         2 eggs
//         1 cup milk
//     butter (greased pan)
//   ```
//
// A line with nothing under it is an ingredient, any other a step. A leading
// `-` or `*` is dropped, so a markdown list reads the same.
//
// It's drawn as a table: the ingredients down the left, one to a row, and
// each step to the right of what it takes in, as tall as those rows. A step
// sits one column past the furthest of its inputs; an input further left
// stretches across to it. The last steps reach the right edge.
import {
  type Fenced,
  findFenced,
} from '@/app/fence';

export type Node = { text: string, inputs: Node[] };

export interface Recipe extends Fenced {
  steps: Node[];
}

// A cell of the drawn table, by the row and column it starts at
export type Cell = {
  text: string,
  step: boolean,
  row: number,
  col: number,
  rowSpan: number,
  colSpan: number,
};

export const findRecipes = (body: string): Recipe[] =>
  findFenced(body, 'recipe').map(block => ({
    ...block,
    steps: parseRecipe(block.content),
  }));

const indentOf = (line: string) =>
  line.match(/^\s*/)![0].replace(/\t/g, '  ').length;

// Each line goes under the nearest line above it that is indented less: a
// line's inputs are the lines after it, up to the next one indented no deeper
export const parseRecipe = (text: string): Node[] => {
  const lines = text.split('\n')
    .filter(line => line.trim())
    .map(line => ({
      indent: indentOf(line),
      text: line.trim().replace(/^[-*]\s+/, ''),
    }));
  // the lines in [from, to), each with the deeper ones after it as inputs
  const nodes = (from: number, to: number): Node[] => {
    if (from >= to) return [];
    const { indent, text } = lines[from];
    const next = lines.findIndex((l, i) => i > from && l.indent <= indent);
    const end = next === -1 || next > to ? to : next;
    return [{ text, inputs: nodes(from + 1, end) }, ...nodes(end, to)];
  };
  return nodes(0, lines.length);
};

const depth = (node: Node): number =>
  node.inputs.length ? 1 + Math.max(...node.inputs.map(depth)) : 0;

const leaves = (node: Node): number =>
  node.inputs.length ? node.inputs.reduce((n, i) => n + leaves(i), 0) : 1;

export const columns = (steps: Node[]): number =>
  steps.length ? 1 + Math.max(...steps.map(depth)) : 0;

// every node's cell, a node's before its inputs'
export const layout = (steps: Node[]): Cell[] => {
  const width = columns(steps);
  const place = (node: Node, row: number, reach: number): Cell[] => {
    const col = depth(node);
    const rows = node.inputs.reduce<{ at: number, cells: Cell[] }>(
      ({ at, cells }, input) => ({
        at: at + leaves(input),
        cells: [...cells, ...place(input, at, col)],
      }),
      { at: row, cells: [] },
    );
    return [
      {
        text: node.text,
        step: node.inputs.length > 0,
        row,
        col,
        rowSpan: leaves(node),
        colSpan: reach - col,
      },
      ...rows.cells,
    ];
  };
  return steps.reduce<{ at: number, cells: Cell[] }>(
    ({ at, cells }, step) => ({
      at: at + leaves(step),
      cells: [...cells, ...place(step, at, width)],
    }),
    { at: 0, cells: [] },
  ).cells;
};

// the cells by the table row they start in, left to right, as <tr>s want them
export const tableRows = (cells: Cell[]): Cell[][] => {
  const count = Math.max(0, ...cells.map(c => c.row + c.rowSpan));
  return Array.from(
    { length: count },
    (_, r) => cells.filter(c => c.row === r).sort((a, b) => a.col - b.col),
  );
};
